import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { environment } from '@env/environment';

@Injectable()
export class ApplicationInsightsService {
  private appInsights = new ApplicationInsights({
    config: {
      connectionString: environment.appInsights.connectionString,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      excludeRequestFromAutoTrackingPatterns: [/assets/],
      correlationHeaderExcludedDomains: ['*.clarity.ms'],
    },
  });

  constructor(private router: Router) {
    this.appInsights.loadAppInsights();

    this.appInsights.appInsights.addTelemetryInitializer(
      (telemetryInitializer) => {
        telemetryInitializer.tags['ai.cloud.role'] = environment.moduleName;
      }
    );

    this.appInsights.context.application.ver = environment.appVersion;

    this.router.events
      .pipe(
        filter((event) => event instanceof ResolveEnd),
        map((event) => event as ResolveEnd)
      )
      .subscribe((event) => {
        const activatedComponent = this.getActivatedComponent(event.state.root);
        if (activatedComponent) {
          const routeTemplate = this.getRouteTemplate(event.state.root);

          this.logPageView(
            `${activatedComponent.name} ${routeTemplate}`,
            event.urlAfterRedirects
          );
        }
      });
  }

  setUserId(userId: string): void {
    this.appInsights.setAuthenticatedUserContext(userId);
  }

  clearUserId(): void {
    this.appInsights.clearAuthenticatedUserContext();
  }

  logPageView(name?: string, uri?: string): void {
    this.appInsights.trackPageView({ name, uri });
  }

  logException(exception: Error, severityLevel?: number): void {
    this.appInsights.trackException({ exception, severityLevel });
  }

  //#region Private methods

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';

    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  //#endregion
}
